import { useState, useCallback, useRef } from "react";
import "./AccessRequestHistory.css";
import ToolIcon from "./ToolIcon";

// Access Request History - SailPoint IdentityNow Integration
const API_BASE = process.env.REACT_APP_API_BASE || "https://d14e64x71m.execute-api.us-east-1.amazonaws.com/dev";


// ── CSV export helper ─────────────────────────────────────────────────────
function downloadCSV(results, employeeId) {
  const header = `Access Request History For Employee ID: ${employeeId}`;
  const cols = ["Access Profile", "Type", "Date", "Requested By", "Requested For", "Approval Type", "Reviewed By", "State", "Source", "Comments"];
  const escape = (val) => `"${String(val || "").replace(/"/g, '""')}"`;
  const rows = results.map(r => [
    r.accessProfile,
    r.requestType,
    r.requestDate,
    r.requestedBy,
    r.requestedFor,
    r.approvalType,
    r.reviewedBy,
    r.requestState,
    r.source,
    r.comments
  ].map(escape).join(","));
  const csv = [header, cols.map(escape).join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `access-request-history-${employeeId}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Inline employee lookup panel ──────────────────────────────────────────
function EmployeeLookupPanel({ onSelect }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(null);

  const handleSearch = async () => {
    if (!firstName.trim() && !lastName.trim()) {
      setError("Enter at least a first or last name.");
      return;
    }
    setLoading(true);
    setError("");
    setResults(null);
    setSelected(null);
    try {
      const res = await fetch(`${API_BASE}/employee-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `API error ${res.status}`);
      setResults(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSelect = (row) => {
    setSelected(row.employeeNumber);
    onSelect(row.employeeNumber);
  };

  return (
    <div className="as-lookup-panel">
      <div className="as-lookup-title">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        Search by name — click a row to populate the Employee ID field
      </div>
      <div className="as-lookup-inputs">
        <div className="as-lookup-field">
          <label className="control-label">First Name</label>
          <input
            autoFocus
            className="cug-input"
            placeholder="e.g. John"
            value={firstName}
            onChange={e => {
              setFirstName(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
        </div>
        <div className="as-lookup-field">
          <label className="control-label">Last Name</label>
          <input
            className="cug-input"
            placeholder="e.g. Doe"
            value={lastName}
            onChange={e => {
              setLastName(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
        </div>
        <button
          className="compare-btn"
          onClick={handleSearch}
          disabled={loading || (!firstName.trim() && !lastName.trim())}
          style={{ alignSelf: "flex-end" }}
        >
          {loading ? (
            <>
              <span className="spinner" /> Searching…
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Search
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="as-error-bar" style={{ margin: "10px 0 0" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {results !== null && (
        results.length === 0 ? (
          <div className="as-lookup-empty">No employees found. Try different names.</div>
        ) : (
          <div className="as-lookup-table-wrap">
            <div className="as-lookup-thead">
              <div className="as-lth">Employee ID</div>
              <div className="as-lth">First Name</div>
              <div className="as-lth">Last Name</div>
              <div className="as-lth">Email</div>
            </div>
            <div className="as-lookup-tbody">
              {results.map((r, i) => (
                <div
                  key={i}
                  className={`as-lookup-tr ${selected === r.employeeNumber ? "as-lookup-tr-selected" : ""}`}
                  onClick={() => handleSelect(r)}
                >
                  <div className="as-ltd">
                    <span className="as-emp-id-badge">{r.employeeNumber || "—"}</span>
                  </div>
                  <div className="as-ltd">{r.firstName || "—"}</div>
                  <div className="as-ltd">{r.lastName || "—"}</div>
                  <div className="as-ltd as-ltd-muted">{r.email || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
}

// ── Results table ─────────────────────────────────────────────────────────
function ResultsTable({ results, searchText }) {
  const filtered = results.filter(r => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      r.accessProfile?.toLowerCase().includes(q) ||
      r.requestType?.toLowerCase().includes(q) ||
      r.requestedBy?.toLowerCase().includes(q) ||
      r.requestedFor?.toLowerCase().includes(q) ||
      r.approvalType?.toLowerCase().includes(q) ||
      r.reviewedBy?.toLowerCase().includes(q) ||
      r.source?.toLowerCase().includes(q) ||
      r.sourceApplication?.toLowerCase().includes(q) ||
      r.comments?.toLowerCase().includes(q)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getRequestTypeBadge = (requestType) => {
    if (requestType === "GRANT_ACCESS") {
      return <span className="ar-badge ar-badge-grant">GRANT</span>;
    } else if (requestType === "REVOKE_ACCESS") {
      return <span className="ar-badge ar-badge-revoke">REVOKE</span>;
    }
    return <span className="ar-badge">{requestType}</span>;
  };

  const getRequestStateBadge = (state) => {
    if (state === "REQUEST_COMPLETED") {
      return <span className="ar-state-badge ar-state-completed">COMPLETED</span>;
    } else if (state === "CANCELLED") {
      return <span className="ar-state-badge ar-state-cancelled">CANCELLED</span>;
    } else if (state === "PENDING") {
      return <span className="ar-state-badge ar-state-pending">PENDING</span>;
    }
    return <span className="ar-state-badge">{state}</span>;
  };

  return (
    <div className="ar-results-section">
      <div className="ar-results-count">
        {filtered.length} {filtered.length === 1 ? "request" : "requests"}
        {searchText && ` (filtered from ${results.length})`}
      </div>

      <div className="ar-table-container">
        <table className="ar-table">
          <thead>
            <tr>
              <th>Access Profile</th>
              <th>Type</th>
              <th>Date</th>
              <th>Requested By</th>
              <th>Requested For</th>
              <th>Approval Type</th>
              <th>Reviewed By</th>
              <th>State</th>
              <th>Source</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="10" className="ar-empty-cell">
                  {searchText ? "No requests match your search." : "No access request history found."}
                </td>
              </tr>
            ) : (
              filtered.map((row, idx) => (
                <tr key={idx}>
                  <td className="ar-cell-profile">{row.accessProfile || "—"}</td>
                  <td>{getRequestTypeBadge(row.requestType)}</td>
                  <td className="ar-cell-date">{formatDate(row.requestDate)}</td>
                  <td>{row.requestedBy || "—"}</td>
                  <td>{row.requestedFor || "—"}</td>
                  <td className="ar-cell-approval">{row.approvalType || "None"}</td>
                  <td>{row.reviewedBy || "None"}</td>
                  <td>{getRequestStateBadge(row.requestState)}</td>
                  <td className="ar-cell-source">{row.source || row.sourceApplication || "—"}</td>
                  <td className="ar-cell-comments" title={row.comments || "—"}>{row.comments || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="as-table-footer">
        Showing {filtered.length} of {results.length} requests
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function AccessRequestHistory() {
  const [employeeId, setEmployeeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [showLookup, setShowLookup] = useState(false);
  const inputRef = useRef(null);

  const handleSearch = useCallback(async () => {
    const id = employeeId.trim();
    if (!id) {
      setError("Please enter an Employee ID.");
      return;
    }
    setLoading(true);
    setError("");
    setResults(null);
    setSearchText("");
    setShowLookup(false);
    try {
      const res = await fetch(`${API_BASE}/access-request-history`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employeeId: id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `API error ${res.status}`);
      setResults(data.results || []);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleReset = () => {
    setEmployeeId("");
    setResults(null);
    setError("");
    setSearchText("");
    setShowLookup(false);
    inputRef.current?.focus();
  };

  const handleExport = () => {
    if (!results) return;
    const visible = results.filter(r => {
      if (!searchText) return true;
      const q = searchText.toLowerCase();
      return (
        r.accessProfile?.toLowerCase().includes(q) ||
        r.requestType?.toLowerCase().includes(q) ||
        r.requestedBy?.toLowerCase().includes(q) ||
        r.requestedFor?.toLowerCase().includes(q) ||
        r.approvalType?.toLowerCase().includes(q) ||
        r.reviewedBy?.toLowerCase().includes(q) ||
        r.source?.toLowerCase().includes(q) ||
        r.sourceApplication?.toLowerCase().includes(q) ||
        r.comments?.toLowerCase().includes(q)
      );
    });
    downloadCSV(visible, employeeId);
  };

  const handleLookupSelect = (empId) => {
    setEmployeeId(empId);
    setShowLookup(false);
    setError("");
    inputRef.current?.focus();
  };

  return (
    <div className="as-root ar-request-history-page">
      {/* ── Header ── */}
      <div className="as-header">
        <div className="as-header-inner">
          <div className="logo-mark logo-mark-route"><ToolIcon toolId="access-request-history" size={19} /></div>
          <div>
            <div className="as-title">Access Request History</div>
            <div className="as-subtitle">View GRANT and REVOKE history from SailPoint IdentityNow</div>
          </div>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="as-search-bar">
        <div className="as-search-inner">
          <div className="as-search-group">
            <div className="as-search-row">
              <label className="control-label" style={{ marginBottom: 0 }}>Employee ID</label>
              <input
                ref={inputRef}
                className="cug-input as-id-input"
                placeholder="Enter Employee ID…"
                value={employeeId}
                onChange={e => {
                  setEmployeeId(e.target.value);
                  setError("");
                }}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              <button className="compare-btn" onClick={handleSearch} disabled={loading || !employeeId.trim()}>
                {loading ? (
                  <>
                    <span className="spinner" /> Loading…
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    Get History
                  </>
                )}
              </button>
              <button className="cug-reset-btn" onClick={handleReset}>
                Reset
              </button>
              <button
                className={`as-lookup-toggle ${showLookup ? "as-lookup-toggle-active" : ""}`}
                onClick={() => setShowLookup(v => !v)}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
                {showLookup ? "Hide Name Search" : "Don't know the Employee ID?"}
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  style={{ transform: showLookup ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Search in results */}
              <div className="as-search-inline" style={{ marginLeft: '12px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  className="as-search-inline-input"
                  placeholder="Search in results…"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  disabled={!results || results.length === 0}
                />
                {searchText && (
                  <button className="as-search-clear" onClick={() => setSearchText("")}>
                    ✕
                  </button>
                )}
              </div>

              {/* Export CSV */}
              <button className="as-export-btn" onClick={handleExport} disabled={!results || results.length === 0}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {showLookup && (
          <div className="as-lookup-wrap">
            <EmployeeLookupPanel onSelect={handleLookupSelect} />
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="as-error-bar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {/* ── Info panel ── */}
      {results === null && !loading && !error && (
        <div className="as-info-panel">
          <div className="as-info-title">What this tool does</div>
          <div className="as-info-body">
            <p>Enter an Employee ID to view the complete history of access requests (GRANT and REVOKE) from SailPoint IdentityNow.</p>
            <div className="as-info-features">
              {[
                ["🔍", "Search across all columns instantly"],
                ["📥", "Export filtered results to CSV"],
                ["📋", "View approval details and comments"],
                ["🔄", "Track request state (Completed/Pending/Cancelled)"],
              ].map(([icon, text]) => (
                <div key={text} className="as-info-feature">
                  <span className="as-feature-icon">{icon}</span>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Results ── */}
      {results !== null && <ResultsTable results={results} searchText={searchText} />}
    </div>
  );
}
